import { castArray, compact, every, isArray, isFunction, isNumber,
         isObject, isUndefined, uniq } from 'lodash';
import CancelButton from '@/components/CancelButton';
import Checkbox from '@/components/Checkbox';
import Combobox from '@/components/Combobox';
import DateTimePicker from '@/components/DateTimePicker';
import FormGroup from '@/components/FormGroup';
import KeywordListInput from '@/components/KeywordListInput';
import TextListInput from '@/components/TextListInput';
import ReadonlyFormField from '@/components/ReadonlyFormField';
import SelectInput from '@/components/SelectInput';
import SubmitButton from '@/components/SubmitButton';
import TextareaInput from '@/components/TextareaInput';
import TextInput from '@/components/TextInput';

const DEFAULTS = {
  default: undefined,
  required: false,
  validators: [],
  parse: value => value,
  serialize: value => value,
  presentIf(value) {
    if (isNumber(value)) return true;
    if (value && !isUndefined(value.length)) return value.length > 0;
    return !!value;
  },
};

class FormBuilder {
  constructor(definitions = {}) {
    this.fields = {};
    Object.keys(definitions).forEach((fieldName) => {
      let definition = definitions[fieldName];
      if (definition === false) definition = { serialize: false };

      const defaults = Object.assign({}, DEFAULTS);
      defaults.as = fieldName;
      defaults.errorsFrom = uniq(compact([
        definition.as,
        definition.loadFrom,
        definition.serializeTo,
        fieldName,
      ]));

      this.fields[fieldName] = Object.assign({}, defaults, definition);
    });
  }

  getFieldNames() {
    return Object.keys(this.fields);
  }

  getRequiredFields() {
    return this.getFieldNames().filter(
      fieldName => this.fields[fieldName].required,
    );
  }

  getValue(model, fieldName) {
    const definition = this.fields[fieldName];
    const modelOrDefault = model || {};
    const value = modelOrDefault[definition.loadFrom || definition.as];
    if (definition.presentIf(value)) {
      return definition.parse(value, modelOrDefault, fieldName);
    }
    if (isFunction(definition.default)) {
      return definition.default(modelOrDefault, fieldName);
    }
    return definition.default;
  }

  fieldIsPresent(model, fieldName) {
    const definition = this.fields[fieldName];
    const value = this.getValue(model, fieldName);
    return definition.presentIf(value);
  }

  async validate(context, formData) {
    const validators = this.getFieldNames().map(
      fieldName => this.validateField(context, formData, fieldName),
    );
    const results = await Promise.all(validators);
    let isValid = true;
    const errors = {};
    results.forEach((result) => {
      if (!result.isValid) {
        errors[result.fieldName] = result.messages;
        isValid = false;
      }
    });
    return { isValid, errors };
  }

  async validateField(context, formData, fieldName) {
    const value = this.getValue(formData, fieldName);
    const validators = castArray(this.fields[fieldName].validators);
    const validations = validators.map(validator =>
      validator.apply(context, [fieldName, value, formData]),
    );
    const results = await FormBuilder.performValidations(validations);
    results.fieldName = fieldName;
    return results;
  }

  static async performValidations(validations) {
    const results = await Promise.all(validations);
    let isValid = true;
    const messages = [];
    results.forEach((result) => {
      if (result && !result.isValid) {
        messages.push(result.message);
        isValid = false;
      }
    });
    return { isValid, messages };
  }

  serializeFormData(formData) {
    const serialized = {};
    this.getFieldNames().forEach((fieldName) => {
      const definition = this.fields[fieldName];
      const value = formData[fieldName];
      if (definition.serialize) {
        const serializedValue = definition.serialize(value);
        serialized[definition.serializeTo || definition.as] = serializedValue;
      }
    });
    return FormBuilder.stripTypenames(serialized);
  }

  static stripTypenames(obj) {
    const newObj = {};
    let value;
    Object.keys(obj).forEach((key) => {
      value = obj[key];
      if (key !== '__typename') {
        if (value instanceof File || value instanceof FileList) {
          newObj[key] = value;
        } else if (isArray(value)) {
          newObj[key] = value.slice(0);
        } else if (isObject(value)) {
          newObj[key] = this.stripTypenames(value);
        } else {
          newObj[key] = value;
        }
      }
    });
    return newObj;
  }

  getModel(model) {
    const result = {};
    this.getFieldNames().forEach((fieldName) => {
      result[fieldName] = this.getValue(model, fieldName);
    });
    return result;
  }

  getErrors(errors) {
    const errorsOrDefault = errors || {};
    const result = {};
    result.base = errorsOrDefault.base || [];
    this.getFieldNames().forEach((fieldName) => {
      const definition = this.fields[fieldName];
      let fieldErrors = [];
      definition.errorsFrom.forEach((source) => {
        const sourceErrors = errorsOrDefault[source] || [];
        fieldErrors = fieldErrors.concat(sourceErrors);
      });
      result[fieldName] = fieldErrors;
    });
    return result;
  }
}

export default (definitions) => {
  const builder = new FormBuilder(definitions);

  return {
    components: {
      CancelButton,
      Checkbox,
      Combobox,
      DateTimePicker,
      FormGroup,
      KeywordListInput,
      ReadonlyFormField,
      SelectInput,
      SubmitButton,
      TextInput,
      TextareaInput,
      TextListInput,
    },
    data() {
      return {
        form: {
          model: builder.getModel(this.model),
          errors: builder.getErrors(this.errors),
        },
        isValidating: false,
      };
    },
    props: {
      model: Object,
      errors: Object,
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    watch: {
      model(model) {
        this.form.model = builder.getModel(model);
      },
      errors(errors) {
        this.form.errors = builder.getErrors(errors);
      },
    },
    computed: {
      formData() {
        const data = {};
        builder.getFieldNames().forEach((fieldName) => {
          data[fieldName] = this.getValue(fieldName);
        });
        return data;
      },
      serializedFormData() {
        return builder.serializeFormData(this.formData);
      },
      isPopulated() {
        return every(
          builder.getRequiredFields(),
          fieldName => builder.fieldIsPresent(this.form.model, fieldName),
        );
      },
      isSubmittable() {
        return !this.disabled && !this.isValidating && this.isPopulated;
      },
      isEditable() {
        return !this.disabled && !this.isValidating;
      },
      isValid() {
        const errors = this.form.errors;
        return errors && errors.length > 0;
      },
      isInvalid() {
        return !this.isValid;
      },
    },
    methods: {
      getValue(fieldName) {
        return this.form.model[fieldName];
      },
      getErrors(fieldName) {
        return this.form.errors[fieldName] || [];
      },
      async validate() {
        this.isValidating = true;
        const formData = Object.assign({}, this.formData);
        this.$emit('validate', formData);
        try {
          const result = await builder.validate(this, formData);
          if (!result.isValid) {
            this.form.errors = result.errors;
          }
          this.isValidating = false;
          this.$emit('validated', {
            formData,
            isValid: result.isValid,
            errors: result.errors,
          });
          return result;
        } catch (error) {
          this.isValidating = false;
          throw error;
        }
      },
      async submit() {
        if (!this.isSubmittable) {
          throw new Error('Form not submittable');
        }
        const operation = await this.validate();
        if (operation.isValid) {
          this.$emit('submit', this.serializedFormData, this.formData);
        }
        return operation;
      },
    },
  };
};



// WEBPACK FOOTER //
// ./mixins/LegacyForm.js